import React, { useState, useEffect, useContext } from "react";
import BqnContext from "common/BqnContext";
import Request from "common/Request";
import Result from "./Result";
import {
  parsePoolMemoryUsage,
  parseDPDKMemoryUsage,
  parseSystemMemoryCluster,
} from "./api.js";
import ActionsContext from "common/ActionsContext";
import { atBqnTarget, bqnIdTarget } from "common/api";
import View from "common/View";
import useDebouncer from "common/hooks/debouncer";

const LOAD_INTERVAL = 3000;

// The memory of the BQN selected in the header, or of the whole cluster. Only
// "show system memory" reports no BQN of its own when it is asked for one, so
// only its data needs the BQN selected to be attributed to it.
export const memoryUsageRetrieval = (mode, bqnId) => {
  const bqnIdShown = bqnIdTarget(mode, bqnId);

  return Promise.all([
    ifCl
      .run(atBqnTarget("show system memory", mode, bqnId))
      .then((response) =>
        parseSystemMemoryCluster(response, mode, bqnIdShown)),
    ifCl
      .run(atBqnTarget("show pkteng memory", mode, bqnId))
      .then((response) => parsePoolMemoryUsage(response, mode)),
    ifCl
      .run(atBqnTarget("show pkteng memory dpdk", mode, bqnId))
      .then((response) => parseDPDKMemoryUsage(response, mode)),
  ]).then(([System, Mpool, Dpdk]) => ({ System, Mpool, Dpdk }));
};

const Report = () => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const mode = clusterStatus?.mode;

  const debounce = useDebouncer(500 /*ms*/);

  useEffect(() => {
    // Nothing is loaded while the BQN context has not loaded the BQN
    // selected: the commands would not carry its option, and their responses
    // would be those of the whole cluster. The effect runs again when it does.
    if (bqnId === undefined) {
      return;
    }

    let active = true;

    const doLoad = () => {
      const req = memoryUsageRetrieval(mode, bqnId).then((data) => {
        if (active) setLiveData(data);
        return data;
      });
      setRequest(req);
    };

    const doBackgroundLoad = () => {
      memoryUsageRetrieval(mode, bqnId)
        .then((data) => {
          if (active) setLiveData(data);
        })
        .catch((err) => console.error("Memory background fetch failed:", err));
    };

    doLoad();
    const intervalId = setInterval(doBackgroundLoad, LOAD_INTERVAL);
    const unsubscribe = actions.recv("do-load", () => debounce(doLoad));

    return () => {
      active = false;
      clearInterval(intervalId);
      unsubscribe();
      debounce.stop();
    };
  }, [mode, bqnId, debounce]);

  return (
    <Request during={request}>
      {() => (liveData ? <Result {...liveData} /> : null)}
    </Request>
  );
};

const MemoryStatus = ({ returnView = null }) => (
  <View title="Memory Usage" returnView={returnView}>
    <Report />
  </View>
);
export default MemoryStatus;
